/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, boolean, timestamp, jsonb, integer } from "drizzle-orm/pg-core";
import { z } from "zod";
import { createInsertSchema } from "drizzle-zod";

// LLM Provider types
export const LLM_PROVIDERS = {
  VERTEX: 'vertex',
  MODEL_GARDEN: 'model_garden',
  LANGCHAIN: 'langchain',
  LLM_SERVICE: 'llm_service',
  TRUSS: 'truss',
  VLLM: 'vllm',
} as const;

export type LLMProvider = typeof LLM_PROVIDERS[keyof typeof LLM_PROVIDERS];

// LLM Vendor types
export const LLM_VENDORS = {
  OPENAI: 'openai',
  COHERE: 'cohere',
  GOOGLE: 'google',
  ANTHROPIC: 'anthropic',
} as const;

export type LLMVendor = typeof LLM_VENDORS[keyof typeof LLM_VENDORS];

// LLM Model Types
export const LLM_MODEL_TYPES = {
  TEXT: 'text',
  CHAT: 'chat',
  EMBEDDING: 'embedding',
  MULTIMODAL: 'multimodal',
  IMAGE: 'image',
} as const;

export type LLMModelType = typeof LLM_MODEL_TYPES[keyof typeof LLM_MODEL_TYPES];

// LLM Input Types
export const LLM_INPUT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  AUDIO: 'audio',
  VIDEO: 'video',
  PDF: 'pdf',
} as const;

export type LLMInputType = typeof LLM_INPUT_TYPES[keyof typeof LLM_INPUT_TYPES];

// Tool Definition Schema - Compatible with multiple LLM providers (Anthropic, Google, OpenAI)
export const LLMToolSchema = z.object({
  name: z.string().min(1, "Tool name is required"),
  description: z.string().min(1, "Tool description is required"),
  input_schema: z.object({
    type: z.literal("object"),
    properties: z.record(z.any()),
    required: z.array(z.string()).optional(),
  }),
});

export type LLMTool = z.infer<typeof LLMToolSchema>;

// Tool Choice Schema
export const ToolChoiceSchema = z.union([
  z.literal("auto"),
  z.literal("none"), 
  z.literal("any"),
  z.object({
    type: z.literal("tool"),
    name: z.string(),
  }),
]);

export type ToolChoice = z.infer<typeof ToolChoiceSchema>;

// Prompts
export const prompts = pgTable("prompts", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  template: text("template").notNull(),
  defaultParams: jsonb("default_params"), // Default parameters for the prompt template
  modelId: text("model_id"), // Optional default model to use
  userId: integer("user_id"), // Optional association with a user
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  tags: text("tags").array(),
  isSystem: boolean("is_system").default(false), // Whether this is a system prompt that can't be modified
  returnType: text("return_type").default("text"), // The type of response expected ('text' or 'json')
  tools: jsonb("tools"), // Array of LLMTool objects (compatible with Anthropic, Google, OpenAI)
  toolChoice: text("tool_choice").default("auto"), // JSON string representation of ToolChoice
  disableParallelToolUse: boolean("disable_parallel_tool_use").default(false), // When true, LLM will use at most one tool per response
});

export const insertPromptSchema = createInsertSchema(prompts).omit({ id: true, createdAt: true, updatedAt: true });
export type Prompt = typeof prompts.$inferSelect;
export type InsertPrompt = z.infer<typeof insertPromptSchema>;


// LLM Provider Configuration
export const LLMProviderSchema = z.object({
  enabled: z.boolean().default(true),
  envFlag: z.string().optional(),
  description: z.string().optional(),
  modelParams: z.record(z.any()).optional(),
});

export type LLMProviderConfig = z.infer<typeof LLMProviderSchema>;

// LLM Vendor Configuration
export const LLMVendorSchema = z.object({
  enabled: z.boolean().default(false),
  apiKey: z.string().optional(),
  envFlag: z.string().optional(),
  description: z.string().optional(),
});

export type LLMVendorConfig = z.infer<typeof LLMVendorSchema>;

// Provider-specific model names type
// Maps provider identifiers to their specific model names
// e.g., { "vertex_anthropic": "claude-3-5-sonnet-v2@20241022" }
export type ProviderModelNames = Record<string, string>;

// LLM Models table
export const llmModels = pgTable("llm_models", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  provider: text("provider").notNull(),
  vendor: text("vendor"),
  modelName: text("model_name").notNull(),
  modelClass: text("model_class"),
  modelEndpoint: text("model_endpoint"),
  description: text("description"),
  capabilities: jsonb("capabilities").$type<string[]>(),
  dateAdded: timestamp("date_added"),
  modelParams: jsonb("model_params"), // Store as JSONB for better querying
  contextLength: integer("context_length"),
  enabled: boolean("enabled").default(true),
  isChat: boolean("is_chat").default(false),
  isMulti: boolean("is_multi"),
  supportedInputTypes: jsonb("supported_input_types").$type<string[]>(), // Store as JSONB for better querying
  agentEnabled: boolean("agent_enabled").default(false), // Whether this model can be used by agents
  providerModelNames: jsonb("provider_model_names").$type<ProviderModelNames>(), // Provider-specific model names (e.g., vertex_anthropic -> claude-3-5-sonnet@20241022)
});

// LLM Model Schema
export type LLMModel = typeof llmModels.$inferSelect;
export const insertLLMModelSchema = createInsertSchema(llmModels).omit({ id: true });
export type InsertLLMModel = z.infer<typeof insertLLMModelSchema>;
